import { useEffect, useState, useCallback } from "react";
import { ScrollText, Search, Loader2, User, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/adminApi";

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/audit", { params: { q, action, limit: 300 } });
      setLogs(r.data.logs || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [q, action]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-audit-view">
      <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><ScrollText className="h-6 w-6 text-blue-600" /> Audit Logs</h1>
      <p className="text-slate-500 mt-1">Every administrative action is permanently recorded.</p>

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search admin, customer or detail…" className="pl-9" />
        </div>
        <Input data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} placeholder="Filter by action (e.g. subscription)" className="w-64" />
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-semibold px-4 py-3">When</th>
              <th className="text-left font-semibold px-4 py-3">Administrator</th>
              <th className="text-left font-semibold px-4 py-3">Action</th>
              <th className="text-left font-semibold px-4 py-3">Customer</th>
              <th className="text-left font-semibold px-4 py-3">Detail</th>
              <th className="text-left font-semibold px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={6} className="py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && logs.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-400">No audit entries match.</td></tr>}
            {!loading && logs.map((l) => (
              <tr key={l.id} data-testid="audit-row" className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{l.admin_name || "—"}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-blue-50 text-blue-700 text-xs font-semibold px-2 py-0.5">{l.action}</span></td>
                <td className="px-4 py-3 text-slate-600">{l.customer_name || "—"}</td>
                <td className="px-4 py-3 text-slate-500 max-w-xs truncate">
                  {l.detail}
                  {l.before && l.after && <span className="ml-1 text-xs text-slate-400 inline-flex items-center gap-1">({JSON.stringify(l.before).slice(0, 30)} <ArrowRight className="h-3 w-3" /> {JSON.stringify(l.after).slice(0, 30)})</span>}
                </td>
                <td className="px-4 py-3 text-slate-400 text-xs">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
